"use client";

import * as React from "react";
import {
  Bar as RechartsBar,
  BarChart as RechartsBarChart,
  CartesianGrid,
  Legend as RechartsLegend,
  Line as RechartsLine,
  LineChart as RechartsLineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
  type DefaultLegendContentProps,
  type DefaultTooltipContentProps,
  type TooltipValueType,
} from "recharts";
import { mergeClassNames } from "./class-names";

export const chartSeriesColors = {
  lead: "var(--color-chart-1)",
  supportingOne: "var(--color-chart-2)",
  supportingTwo: "var(--color-chart-3)",
  supportingThree: "var(--color-chart-4)",
  supportingFour: "var(--color-chart-5)",
  supportingFive: "var(--color-chart-6)",
} as const;

export type ChartSeriesColor =
  (typeof chartSeriesColors)[keyof typeof chartSeriesColors];

export type ChartColorReference = `var(--color-${string})`;

interface ChartConfigItem {
  readonly label: React.ReactNode;
  readonly color: ChartSeriesColor;
  readonly icon?: React.ComponentType;
}

export type ChartConfig = Readonly<Record<string, ChartConfigItem>>;

interface ChartContextValue {
  readonly config: ChartConfig;
  readonly description: string;
  readonly title: string;
}

const ChartContext = React.createContext<ChartContextValue | null>(null);

function useChart(): ChartContextValue {
  const context = React.useContext(ChartContext);
  if (context === null) {
    throw new Error("Chart components must be used within ChartContainer.");
  }
  return context;
}

const defaultInitialDimension = { width: 320, height: 256 } as const;

export interface ChartContainerProps extends Omit<
  React.ComponentPropsWithoutRef<"figure">,
  "aria-describedby" | "aria-label" | "aria-labelledby" | "children" | "title"
> {
  readonly chartClassName?: string | undefined;
  readonly children: React.ReactElement;
  readonly config: ChartConfig;
  readonly dataTable: React.ReactElement<
    React.ComponentPropsWithoutRef<"table">,
    "table"
  >;
  readonly dataTableClassName?: string;
  readonly description: string;
  readonly initialDimension?: {
    readonly width: number;
    readonly height: number;
  };
  readonly summary: React.ReactElement | string;
  readonly summaryClassName?: string;
  readonly title: string;
}

function createChartStyle(config: ChartConfig): React.CSSProperties {
  const style: React.CSSProperties & Record<string, string | number> = {};
  for (const [key, item] of Object.entries(config)) {
    style[`--color-${key}`] = item.color;
  }
  return style;
}

export const ChartContainer = React.forwardRef<
  HTMLElement,
  ChartContainerProps
>(
  (
    {
      chartClassName,
      children,
      className,
      config,
      dataTable,
      dataTableClassName,
      description,
      initialDimension = defaultInitialDimension,
      summary,
      summaryClassName,
      title,
      ...props
    },
    ref,
  ) => {
    const titleId = React.useId();
    const descriptionId = React.useId();
    const summaryId = React.useId();

    return (
      <ChartContext.Provider value={{ config, description, title }}>
        <figure
          ref={ref}
          data-slot="chart-container"
          aria-labelledby={titleId}
          aria-describedby={`${descriptionId} ${summaryId}`}
          className={mergeClassNames("grid min-w-0 gap-3", className)}
          {...props}
        >
          <span id={titleId} className="sr-only">
            {title}
          </span>
          <span id={descriptionId} className="sr-only">
            {description}
          </span>
          <div
            data-slot="chart-visual"
            style={createChartStyle(config)}
            className={mergeClassNames(
              "h-64 min-w-0 overflow-hidden text-xs [&_.recharts-cartesian-axis-line]:stroke-[var(--color-chart-axis)] [&_.recharts-cartesian-axis-tick-line]:stroke-[var(--color-chart-axis)] [&_.recharts-cartesian-axis-tick_text]:fill-[var(--color-chart-label-secondary)] [&_.recharts-cartesian-grid_line]:stroke-[var(--color-chart-grid)] [&_.recharts-curve.recharts-tooltip-cursor]:stroke-[var(--color-chart-grid)] [&_.recharts-label]:fill-[var(--color-chart-label-primary)] [&_.recharts-rectangle.recharts-tooltip-cursor]:fill-[var(--color-bg-subtle)] [&_.recharts-reference-line_line]:stroke-[var(--color-chart-axis)] [&_.recharts-surface]:rounded-md [&_.recharts-surface]:outline-none [&_.recharts-surface:focus-visible]:ring-2 [&_.recharts-surface:focus-visible]:ring-focus [&_.recharts-surface:focus-visible]:ring-offset-2 [&_.recharts-surface:focus-visible]:ring-offset-surface",
              chartClassName,
            )}
          >
            <ResponsiveContainer initialDimension={initialDimension}>
              {children}
            </ResponsiveContainer>
          </div>
          <figcaption
            id={summaryId}
            data-slot="chart-summary"
            className={mergeClassNames(
              "min-w-0 rounded-md bg-subtle p-3 text-body text-secondary [overflow-wrap:anywhere]",
              summaryClassName,
            )}
          >
            {summary}
          </figcaption>
          <div
            data-slot="chart-data-table"
            className={mergeClassNames(
              "min-w-0 overflow-x-auto rounded-lg border border-border-subtle bg-surface",
              dataTableClassName,
            )}
          >
            {dataTable}
          </div>
        </figure>
      </ChartContext.Provider>
    );
  },
);
ChartContainer.displayName = "ChartContainer";

export type ChartTooltipProps = Omit<
  React.ComponentProps<typeof RechartsTooltip>,
  "isAnimationActive"
>;

export function ChartTooltip(props: ChartTooltipProps) {
  return <RechartsTooltip {...props} isAnimationActive="auto" />;
}

type ChartTooltipName = number | string;

type ChartTooltipBaseProps = Pick<
  DefaultTooltipContentProps<TooltipValueType, ChartTooltipName>,
  "formatter" | "label" | "labelClassName" | "labelFormatter" | "payload"
>;

export type ChartTooltipContentProps = ChartTooltipBaseProps & {
  readonly active?: boolean;
  readonly accessibilityLayer?: boolean;
  readonly className?: string;
  readonly color?: ChartSeriesColor;
  readonly hideIndicator?: boolean;
  readonly hideLabel?: boolean;
  readonly indicator?: "dot" | "line" | "dashed";
  readonly labelKey?: string;
  readonly nameKey?: string;
};

function getProperty(value: unknown, key: string): unknown {
  if (typeof value !== "object" || value === null) {
    return undefined;
  }
  return Reflect.get(value, key);
}

function getStringProperty(value: unknown, key: string): string | undefined {
  const property = getProperty(value, key);
  return typeof property === "string" ? property : undefined;
}

function getPayloadConfig(
  config: ChartConfig,
  payload: unknown,
  key: string,
): ChartConfigItem | undefined {
  const nestedPayload = getProperty(payload, "payload");
  const configKey =
    getStringProperty(payload, key) ?? getStringProperty(nestedPayload, key);
  return configKey === undefined ? config[key] : config[configKey];
}

function formatChartValue(value: TooltipValueType): string {
  const locale =
    typeof document === "undefined" || document.documentElement.lang === ""
      ? undefined
      : document.documentElement.lang;
  if (typeof value === "number") {
    return value.toLocaleString(locale);
  }
  if (typeof value === "string") {
    return value;
  }
  return value
    .map((entry) =>
      typeof entry === "number" ? entry.toLocaleString(locale) : entry,
    )
    .join(" – ");
}

export function ChartTooltipContent({
  active,
  accessibilityLayer = false,
  className,
  color,
  formatter,
  hideIndicator = false,
  hideLabel = false,
  indicator = "dot",
  label,
  labelClassName,
  labelFormatter,
  labelKey,
  nameKey,
  payload,
}: ChartTooltipContentProps) {
  const { config } = useChart();

  if (active !== true || payload === undefined || payload.length === 0) {
    return null;
  }

  const firstItem = payload[0];
  const firstKey = String(
    labelKey ?? firstItem?.dataKey ?? firstItem?.name ?? "value",
  );
  const firstConfig = getPayloadConfig(config, firstItem, firstKey);
  const resolvedLabel =
    typeof label === "string" || typeof label === "number"
      ? (config[String(label)]?.label ?? label)
      : (firstConfig?.label ?? label);
  const tooltipLabel = hideLabel
    ? null
    : labelFormatter === undefined
      ? resolvedLabel
      : labelFormatter(resolvedLabel, payload);

  return (
    <div
      data-slot="chart-tooltip-content"
      role={accessibilityLayer ? "status" : undefined}
      className={mergeClassNames(
        "grid min-w-32 max-w-xs items-start gap-1.5 overflow-hidden rounded-lg border border-[var(--color-chart-axis)] bg-[var(--color-chart-tooltip-background)] px-3 py-2 text-start text-xs text-[var(--color-chart-tooltip-foreground)] shadow-[var(--elevation-2)]",
        className,
      )}
    >
      {tooltipLabel === null || tooltipLabel === undefined ? null : (
        <div
          className={mergeClassNames(
            "min-w-0 break-words font-semibold [overflow-wrap:anywhere]",
            labelClassName,
          )}
        >
          {tooltipLabel}
        </div>
      )}
      <div className="grid gap-1.5">
        {payload
          .filter((item) => item.type !== "none")
          .map((item, index) => {
            const key = String(nameKey ?? item.name ?? item.dataKey ?? "value");
            const itemConfig = getPayloadConfig(config, item, key);
            const itemColor =
              color ??
              itemConfig?.color ??
              getStringProperty(getProperty(item, "payload"), "fill") ??
              item.color ??
              "var(--color-chart-axis)";
            const itemName = itemConfig?.label ?? item.name;
            const itemValue = item.value;

            return (
              <div
                key={`${item.graphicalItemId}-${key}`}
                className="flex min-w-0 items-start gap-2"
              >
                {hideIndicator ? null : (
                  <span
                    aria-hidden="true"
                    className={mergeClassNames(
                      "mt-1 shrink-0 rounded-xs",
                      indicator === "dot" && "size-2.5",
                      indicator === "line" && "h-3 w-1",
                      indicator === "dashed" &&
                        "h-3 w-0 border border-dashed bg-transparent",
                    )}
                    style={{
                      backgroundColor:
                        indicator === "dashed" ? undefined : itemColor,
                      borderColor: itemColor,
                    }}
                  />
                )}
                {formatter !== undefined && itemValue !== undefined ? (
                  formatter(itemValue, item.name, item, index, payload)
                ) : (
                  <div className="flex min-w-0 flex-1 flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                    <span className="min-w-0 break-words opacity-75 [overflow-wrap:anywhere]">
                      {itemName}
                    </span>
                    {itemValue === undefined ? null : (
                      <span className="break-all font-mono font-semibold tabular-nums">
                        {formatChartValue(itemValue)}
                        {item.unit}
                      </span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
      </div>
    </div>
  );
}

export const ChartLegend = RechartsLegend;

export interface ChartLegendContentProps {
  readonly className?: string;
  readonly hideIcon?: boolean;
  readonly nameKey?: string;
  readonly payload?: DefaultLegendContentProps["payload"];
  readonly verticalAlign?: DefaultLegendContentProps["verticalAlign"];
}

export function ChartLegendContent({
  className,
  hideIcon = false,
  nameKey,
  payload,
  verticalAlign = "bottom",
}: ChartLegendContentProps) {
  const { config } = useChart();
  if (payload === undefined || payload.length === 0) {
    return null;
  }

  return (
    <div
      data-slot="chart-legend-content"
      className={mergeClassNames(
        "flex min-w-0 flex-wrap items-center justify-center gap-x-4 gap-y-2 text-start text-xs text-[var(--color-chart-label-secondary)]",
        verticalAlign === "top" ? "pb-3" : "pt-3",
        className,
      )}
    >
      {payload
        .filter((item) => item.type !== "none")
        .map((item) => {
          const key = String(nameKey ?? item.dataKey ?? item.value ?? "value");
          const itemConfig = getPayloadConfig(config, item, key);
          const Icon = itemConfig?.icon;
          const itemColor =
            itemConfig?.color ?? item.color ?? "var(--color-chart-axis)";

          return (
            <span
              key={key}
              className="inline-flex min-w-0 items-center gap-1.5"
            >
              {Icon !== undefined && !hideIcon ? (
                <span
                  aria-hidden="true"
                  className="size-3 shrink-0 [&>svg]:size-3"
                >
                  <Icon />
                </span>
              ) : hideIcon ? null : (
                <span
                  aria-hidden="true"
                  className="size-2.5 shrink-0 rounded-xs"
                  style={{ backgroundColor: itemColor }}
                />
              )}
              <span className="min-w-0 break-words [overflow-wrap:anywhere]">
                {itemConfig?.label ?? item.value}
              </span>
            </span>
          );
        })}
    </div>
  );
}

export type BarProps = Omit<
  React.ComponentProps<typeof RechartsBar>,
  "fill" | "isAnimationActive"
> & {
  readonly fill: ChartColorReference;
};

export function Bar(props: BarProps) {
  return <RechartsBar {...props} isAnimationActive="auto" />;
}

export type LineProps = Omit<
  React.ComponentProps<typeof RechartsLine>,
  "isAnimationActive" | "stroke"
> & {
  readonly stroke: ChartColorReference;
};

export function Line(props: LineProps) {
  return <RechartsLine {...props} isAnimationActive="auto" />;
}

export type BarChartProps = Omit<
  React.ComponentProps<typeof RechartsBarChart>,
  "accessibilityLayer" | "desc" | "title"
>;

export function BarChart(props: BarChartProps) {
  const { description, title } = useChart();
  const accessibleProps = {
    ...props,
    accessibilityLayer: true,
    desc: description,
    title,
  };
  return <RechartsBarChart {...accessibleProps} />;
}

export type LineChartProps = Omit<
  React.ComponentProps<typeof RechartsLineChart>,
  "accessibilityLayer" | "desc" | "title"
>;

export function LineChart(props: LineChartProps) {
  const { description, title } = useChart();
  const accessibleProps = {
    ...props,
    accessibilityLayer: true,
    desc: description,
    title,
  };
  return <RechartsLineChart {...accessibleProps} />;
}

export { CartesianGrid, XAxis, YAxis };
